import React, { useContext, useEffect, useState } from "react";
import {
  Button,
  Tag,
  message,
  Descriptions,
  List,
  Typography,
  Tabs,
  Spin,
  Result,
} from "antd";
import {
  UserOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import Text from "antd/lib/typography/Text";

import { DateFormat } from "../../helpers";
import { opsApiCall, getOpsToken } from "../../helpers/rapsapOpsAuth";
import CardContainer from "../../components/Card";
import TableContainer from "../../components/Table";
import Sidebar from "../../components/Sidebar";
import DeliveryStatsCards from "../../components/DeliveryStatsCards";
import { useDeliveryStats } from "../../hooks/useDeliveryStats";
import { StoreContext } from "../../context";

export default function Packages() {
  const { storeId } = useContext(StoreContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  const [packageList, setPackageList] = useState([]);
  const [savedPayload, setSavedPayload] = useState({});
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [currentFilter, setCurrentFilter] = useState("all");
  const [searchingById, setSearchingById] = useState(false);

  // Use the custom hook for stats
  const { deliveryData, loading: statsLoading, fetchStats } = useDeliveryStats();

  // Filter options for tag buttons
  const filterList = [
    { label: "Packed", value: "packed" },
    { label: "Out For Delivery", value: "out_for_delivery" },
    { label: "Reschedule", value: "reschedule" },
    { label: "Delivered", value: "delivered" },
    { label: "Arrived", value: "arrived" },
  ];

  useEffect(() => {
    initializeAuth();
  }, [storeId]);

  const initializeAuth = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = await getOpsToken();
      if (token) {
        console.log("Authentication initialized, calling getPackageList and fetchStats...");
        await Promise.all([
          getPackageList(savedPayload),
          fetchStats(false, storeId || "1")
        ]);
      } else {
        throw new Error("Failed to authenticate");
      }
    } catch (error) {
      console.error("Error initializing package auth:", error);
      setError("Failed to initialize authentication. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Search by Order ID function
  const searchByOrderId = async (orderId) => {
    console.log("Searching by Order ID:", orderId);
    setSearchingById(true);
    setLoading(true);
    try {
      const searchPayload = {
        order_id: orderId,
        store_id: storeId || "1",
        type: "admin",
      };

      const res = await opsApiCall("/rapid/getOrderById", searchPayload);
      console.log("Search by ID response:", res);

      if (res?.success && res.data) {
        const orderData = Array.isArray(res.data) ? res.data : [res.data];
        setPackageList(orderData);
        setTotal(orderData.length);
        message.success(`Found order with ID: ${orderId}`);
      } else {
        setPackageList([]);
        setTotal(0);
        message.info(`No order found with ID: ${orderId}`);
      }
    } catch (error) {
      console.error("Error searching by order ID:", error);
      setPackageList([]);
      setTotal(0);
      message.error("Failed to search by order ID");
    }
    setSearchingById(false);
    setLoading(false);
  };

  const getPackageList = async (payload = {}) => {
    console.log("getPackageList called with payload:", payload);
    setLoading(true);
    setSearchingById(false);
    setError(null);

    try {
      // Check if this is a search by keyword (Order ID)
      if (payload.keyword && /^\d+$/.test(payload.keyword.trim())) {
        await searchByOrderId(payload.keyword.trim());
        return;
      }

      let packagePayload = {
        page: payload.page || 0,
        size: payload.size || 12,
        store_id: storeId || "1",
        type: "admin",
        ...payload,
      };

      // Add filter if not "all"
      if (currentFilter !== "all") {
        packagePayload.status = currentFilter;
      }

      const res = await opsApiCall("/rapid/getPackages", packagePayload);
      console.log("Package API response:", res);

      if (Array.isArray(res?.data)) {
        console.log("Setting package list:", res.data);
        setPackageList(res.data);
        setTotal(res.meta?.total || res.data.length);
      } else {
        throw new Error(res?.msg || "No data received from server");
      }

      setSavedPayload(payload);
    } catch (error) {
      console.error("Error fetching package list:", error);
      setError("Failed to load package data. Please try again.");
      setPackageList([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleViewPackage = (packageData) => {
    setSelectedPackage(packageData);
    setSidebarVisible(true);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "packed":
        return "orange";
      case "out_for_delivery":
        return "blue";
      case "delivered":
        return "green";
      case "reschedule":
        return "red";
      case "arrived":
        return "purple";
      default:
        return "default";
    }
  };

  const getStatusText = (status) => {
    switch (status?.toLowerCase()) {
      case "out_for_delivery":
        return "Out For Delivery";
      case "reschedule":
        return "Reschedule";
      default:
        return status?.charAt(0).toUpperCase() + status?.slice(1) || "N/A";
    }
  };

  // Package Details Component
  const PackageDetails = ({ packageData }) => {
    const [activeTab, setActiveTab] = useState("items");

    if (!packageData) return null;

    return (
      <div style={{ padding: "16px 0" }}>
        {/* Package Information */}
        <Descriptions
          title="Package Information"
          bordered
          size="small"
          column={1}
          style={{ marginBottom: "24px" }}
        >
          <Descriptions.Item label="Order ID">
            {packageData.order_id}
          </Descriptions.Item>
          <Descriptions.Item label="Package ID">
            {packageData.package_id || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Store">
            {packageData.store_name || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={getStatusColor(packageData.order_status)}>
              {getStatusText(packageData.order_status)}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Created Date">
            {DateFormat(packageData.created_at)}
          </Descriptions.Item>
        </Descriptions>

        {/* Delivery Address */}
        {packageData.delivery_address && (
          <Descriptions
            title="Customer Delivery Address"
            bordered
            size="small"
            column={1}
            style={{ marginBottom: "24px" }}
          >
            <Descriptions.Item label="Name">
              <UserOutlined style={{ marginRight: 8 }} />
              {packageData.delivery_address.name}
            </Descriptions.Item>
            <Descriptions.Item label="Phone">
              <PhoneOutlined style={{ marginRight: 8 }} />
              {packageData.delivery_address.phone}
            </Descriptions.Item>
            <Descriptions.Item label="Address">
              <EnvironmentOutlined style={{ marginRight: 8 }} />
              {packageData.delivery_address.address1}
              {packageData.delivery_address.address2 &&
                `, ${packageData.delivery_address.address2}`}
            </Descriptions.Item>
            <Descriptions.Item label="City">
              {packageData.delivery_address.city},{" "}
              {packageData.delivery_address.state} -{" "}
              {packageData.delivery_address.pincode}
            </Descriptions.Item>
          </Descriptions>
        )}

        {/* Tabs for Items */}
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <Tabs.TabPane tab="Package Items" key="items">
            <List
              dataSource={packageData.packageDetails || []}
              renderItem={(item, index) => (
                <List.Item key={index}>
                  <List.Item.Meta
                    avatar={<InboxOutlined />}
                    title={item.product_name}
                    description={
                      <div>
                        <Text type="secondary">
                          Category: {item.category_name || "N/A"}
                        </Text>
                        <br />
                        <Text>
                          Quantity: {item.quantity}
                          {item.packed_quantity &&
                            ` | Packed: ${item.packed_quantity}`}
                        </Text>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </Tabs.TabPane>
        </Tabs>
      </div>
    );
  };

  const columns = [
    {
      title: "Order Id",
      dataIndex: "order_id",
      key: "order_id",
      sorter: true,
    },
    {
      title: "Package Id",
      dataIndex: "package_id",
      key: "package_id",
      sorter: true,
    },
    {
      title: "Store",
      dataIndex: "store_name",
      key: "store_name",
    },
    {
      title: "Name",
      key: "name",
      render: (text, record) => record.delivery_address?.name || "N/A",
    },
    {
      title: "Phone",
      key: "phone",
      render: (text, record) => record.delivery_address?.phone || "N/A",
    },
    {
      title: "Order Status",
      dataIndex: "order_status",
      key: "order_status",
      render: (status) => (
        <Tag color={getStatusColor(status)}>{getStatusText(status)}</Tag>
      ),
    },
    {
      title: "Created Date",
      dataIndex: "created_at",
      key: "created_at",
      render: (text) => DateFormat(text),
    },
  ];

  // Error state
  if (error && !loading) {
    return (
      <CardContainer>
        <Result
          status="error"
          title="Failed to Load Package Data"
          subTitle={error}
          extra={[
            <Button type="primary" key="retry" onClick={initializeAuth}>
              Retry
            </Button>,
          ]}
        />
      </CardContainer>
    );
  }

  // Loading state
  if (loading && packageList.length === 0) {
    return (
      <CardContainer>
        <div style={{ textAlign: "center", padding: "50px" }}>
          <Spin size="large" />
          <div style={{ marginTop: "16px" }}>Loading package data...</div>
        </div>
      </CardContainer>
    );
  }

  // Success state
  return (
    <>
      {/* Stats Cards - Show only Packed and Reschedule */}
      <DeliveryStatsCards
        deliveryData={deliveryData}
        showOnly={["packed-orders", "reschedule"]}
      />

      <CardContainer hoverable={false}>
        <TableContainer
          breadcrumbTitle="Package Management"
          loading={loading}
          columns={columns}
          data={packageList}
          total={total}
          searchFor="Order ID, Package ID, Customer Name"
          functionsHandler={getPackageList}
          rowClickHandler={handleViewPackage}
          tagFilterOptions={filterList}
          onTagFilterChange={(filter) => {
            setCurrentFilter(filter);
            getPackageList({ page: 0, size: 12 });
          }}
        />
      </CardContainer>

      <Sidebar
        title="Package Details"
        visible={sidebarVisible}
        onClose={() => {
          setSidebarVisible(false);
          setSelectedPackage(null);
        }}
        width={600}
      >
        <PackageDetails packageData={selectedPackage} />
      </Sidebar>
    </>
  );
}
